import { Button, Row, Typography, Flex } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import IconInfo from '@/assets/icons/icon-info.svg?react';
import IconResult from '@/assets/icons/icon-sim-result.svg?react';
import TagTable from '@/components/RTag';
import Tooltip from '@/components/RTooltip';
import { DrawerType } from '@/types/global';
import { Plant } from '@/types/plant';
import { Scenario } from '@/types/scenario';
import { Simulation } from '@/types/simulations';
import { StorageSystem } from '@/types/storageSystem';

type Props = {
  handleFormsClick: (type: string, assetID: number) => void;
  setDrawerPlant: React.Dispatch<React.SetStateAction<DrawerType<Plant>>>;
  setDrawerScenario: React.Dispatch<React.SetStateAction<DrawerType<Scenario>>>;
  setDrawerStorageSystem: React.Dispatch<
    React.SetStateAction<DrawerType<StorageSystem>>
  >;
};

const useNotRunningSimulationsColumns = (props: Props) => {
  const { t } = useTranslation();
  const columns: ColumnsType<Simulation> = [
    {
      title: t('simulations.table.name'),
      dataIndex: 'name',
      key: 'name',
      className: 'name-column',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, simulation: Simulation) => (
        <Typography.Paragraph
          style={{ width: '100%' }}
          className="simulation-item underline text"
          ellipsis={{
            rows: 1,
            tooltip: name,
            expandable: false,
          }}
        >
          <Link
            to={
              simulation.status === 'SUCCESS'
                ? `/simulations/result/${simulation.id}`
                : '#'
            }
            onClick={(e) => {
              if (simulation.status !== 'SUCCESS') {
                e.preventDefault();
              }
            }}
          >
            {name}
          </Link>
        </Typography.Paragraph>
      ),
    },
    {
      title: t('simulations.table.description'),
      dataIndex: 'description',
      key: 'description',
      className: 'description-column',
      render: (description) => (
        <Typography.Paragraph
          style={{ width: '100%' }}
          className="text"
          ellipsis={{
            rows: 1,
            tooltip: description,
            expandable: false,
          }}
        >
          {description}
        </Typography.Paragraph>
      ),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('simulations.table.storageSystem.title')}
          description={t('simulations.table.storageSystem.description')}
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('simulations.table.storageSystem.title')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'storage',
      key: 'storage',
      sorter: (a, b) => {
        const nameA = a.storage?.name ?? '';
        const nameB = b.storage?.name ?? '';

        return nameA.localeCompare(nameB);
      },
      className: 'storage-system-column',
      render: (dataIndex) => (
        <Typography.Paragraph
          className="simulation-item underline text"
          style={{ width: '100%' }}
          ellipsis={{
            rows: 1,
            tooltip: dataIndex,
            expandable: false,
          }}
          onClick={() => {
            props.setDrawerStorageSystem(dataIndex);
            props.handleFormsClick('storage', dataIndex.id);
          }}
          key={dataIndex.id}
        >{`${dataIndex.name}`}</Typography.Paragraph>
      ),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('simulations.table.scenario.title')}
          description={t('simulations.table.scenario.description')}
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('simulations.table.scenario.title')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'scenario',
      key: 'scenario ',
      className: 'scenario-column',
      sorter: (a, b) => {
        const nameA = a.scenario?.name ?? '';
        const nameB = b.scenario?.name ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (dataIndex) => (
        <Typography.Paragraph
          className="simulation-item underline"
          style={{ maxWidth: '100%' }}
          ellipsis={{
            rows: 1,
            tooltip: dataIndex,
            expandable: false,
          }}
          onClick={() => {
            props.setDrawerScenario(dataIndex);
            props.handleFormsClick('scenario', dataIndex.id);
          }}
          key={dataIndex.id}
        >{`${dataIndex.name}`}</Typography.Paragraph>
      ),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('simulations.table.plants.title')}
          description={t('simulations.table.plants.description')}
        >
          <Row align="middle" justify="start" className="w-100">
            <Typography.Text className="pr-8">
              {t('simulations.table.plants.title')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'plants',
      key: 'plants',
      className: 'plants-column',
      sorter: (a, b) => {
        const nameA = a.plants[0]?.name ?? '';
        const nameB = b.plants[0]?.name ?? '';

        return nameA.localeCompare(nameB);
      },
      render: (dataIndex) => (
        <Flex gap={5}>
          {dataIndex.map((plant: Plant) => (
            <Typography.Paragraph
              className="simulation-item underline"
              style={{ width: '100%' }}
              ellipsis={{
                rows: 1,
                tooltip: plant.name,
                expandable: false,
              }}
              onClick={() => {
                props.setDrawerPlant({ type: 'view', data: plant });
              }}
              key={plant.id}
            >{`${plant.name}`}</Typography.Paragraph>
          ))}
        </Flex>
      ),
    },
    {
      title: (
        <div style={{ minWidth: '120px' }}>
          {t('simulations.table.completionDate.title')}
        </div>
      ),
      dataIndex: 'dateDone',
      key: 'dateDone',
      align: 'start',
      width: 180,
      render: (dataIndex) => moment(dataIndex).format('DD.MM.YYYY HH:mm'),
      sorter: (a, b) =>
        new Date(a.dateDone).getTime() - new Date(b.dateDone).getTime(),
    },
    {
      title: (
        <Tooltip
          className="cursor-pointer"
          trigger="click"
          title={t('simulations.table.status.title')}
          description={t('simulations.table.status.description')}
        >
          <Row
            align="middle"
            justify="start"
            className="w-100"
            style={{ minWidth: '70px' }}
          >
            <Typography.Text className="pr-8">
              {t('simulations.table.status.title')}
            </Typography.Text>
            <IconInfo fill="black" />
          </Row>
        </Tooltip>
      ),
      dataIndex: 'status',
      key: 'status',
      align: 'start',
      width: 114,
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (dataIndex, record) =>
        dataIndex === 'SUCCESS' ? (
          <TagTable type={'success'} />
        ) : dataIndex === 'FAILURE' ? (
          <Tooltip
            title={t('simulations.table.status.error')}
            description={record.errorMessage}
            placement="top"
          >
            <div>
              <TagTable type={'failure'}>{dataIndex}</TagTable>
            </div>
          </Tooltip>
        ) : dataIndex === 'REVOKED' ? (
          <TagTable type={'revoked'}>{dataIndex}</TagTable>
        ) : dataIndex === 'INACTIVE' ? (
          <TagTable type={'inactive'}>{dataIndex}</TagTable>
        ) : dataIndex === 'ACTIVE' ? (
          <TagTable type={'active'}>{dataIndex}</TagTable>
        ) : (
          <></>
        ),
    },
    {
      title: t('simulations.table.results.title'),
      dataIndex: 'actions',
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_, simulation: Simulation) => (
        <>
          {' '}
          {simulation.status === 'SUCCESS' && (
            <Tooltip
              title={t('simulations.table.results.title')}
              description={t('simulations.table.results.description')}
            >
              <Link to={`/simulations/result/${simulation.id}`} target="_blank">
                <Button
                  className="default-icon-button"
                  type="default"
                  icon={<IconResult />}
                />
              </Link>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return columns;
};

export default useNotRunningSimulationsColumns;
